import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import {
  TableContainer, Paper, Table, TableHead, TableRow, TableCell, TableBody,
  Button, Dialog, DialogActions, DialogContent, DialogTitle, TextField,
  Typography, Box, CircularProgress, Stack, MenuItem, Select, FormControl, InputLabel,
  Card, CardContent, Divider,
} from '@mui/material';

const API = 'https://portal.peaksoftcon.com:8081/api/bot';

const blankForm = { id: null, date: '', name: '', notes: '' };

function thisYear() {
  return new Date().getFullYear();
}

export default function AdminHolidays() {
  const [year, setYear] = useState(thisYear());
  const [holidays, setHolidays] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(blankForm);
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/holidays?year=${year}`);
      setHolidays(data || []);
    } catch (err) {
      alert('Tatiller yüklenemedi: ' + (err.response?.data?.error || err.message));
    } finally {
      setLoading(false);
    }
  }, [year]);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => {
    setIsEditing(false);
    setForm({ ...blankForm, date: `${year}-01-01` });
    setOpen(true);
  };

  const openEdit = (h) => {
    setIsEditing(true);
    setForm({ id: h.id, date: h.date, name: h.name || '', notes: h.notes || '' });
    setOpen(true);
  };

  const onFormChange = (field) => (e) => {
    setForm((f) => ({ ...f, [field]: e.target.value }));
  };

  const submit = async () => {
    if (!form.date || !form.name.trim()) {
      alert('Tarih ve ad zorunlu.');
      return;
    }
    setSaving(true);
    try {
      if (isEditing) {
        await axios.put(`${API}/holidays/${form.id}`, {
          date: form.date, name: form.name, notes: form.notes,
        });
      } else {
        await axios.post(`${API}/holidays`, {
          date: form.date, name: form.name, notes: form.notes,
        });
      }
      setOpen(false);
      await load();
    } catch (err) {
      alert('Kaydetme hatası: ' + (err.response?.data?.error || err.message));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (h) => {
    if (!window.confirm(`"${h.name}" tatil kaydını silmek istediğine emin misin?`)) return;
    try {
      await axios.delete(`${API}/holidays/${h.id}`);
      await load();
    } catch (err) {
      alert('Silme hatası: ' + (err.response?.data?.error || err.message));
    }
  };

  const yearOptions = [];
  for (let y = thisYear() - 1; y <= thisYear() + 2; y++) yearOptions.push(y);

  return (
    <Box>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={2}
        alignItems={{ xs: 'stretch', sm: 'center' }}
        mb={2}
        flexWrap="wrap"
      >
        <Typography variant="h4" sx={{ flex: { sm: 1 } }}>Resmi Tatiller</Typography>
        <Stack direction="row" spacing={1} alignItems="center">
          <FormControl size="small">
            <InputLabel id="hol-year">Yıl</InputLabel>
            <Select
              labelId="hol-year"
              label="Yıl"
              value={year}
              onChange={(e) => setYear(e.target.value)}
              sx={{ minWidth: 100 }}
            >
              {yearOptions.map((y) => <MenuItem key={y} value={y}>{y}</MenuItem>)}
            </Select>
          </FormControl>
          <Button onClick={load} disabled={loading} size="small" variant="outlined">
            {loading ? <CircularProgress size={16} /> : 'Yenile'}
          </Button>
          <Button variant="contained" onClick={openCreate}>+ Yeni</Button>
        </Stack>
      </Stack>

      <Typography variant="caption" color="text.secondary" display="block" mb={2}>
        Bu listedeki tarihler post-process'te haftaiçi olsa bile atlanır. Sabit
        millî bayramlar otomatik gelir; Ramazan / Kurban Bayramı gibi her yıl
        kayan dini bayramları admin elle ekler.
      </Typography>

      {/* Desktop / tablet: classic table */}
      <TableContainer component={Paper} sx={{ overflowX: 'auto', display: { xs: 'none', md: 'block' } }}>
        <Table size="small" sx={{ minWidth: 560 }}>
          <TableHead>
            <TableRow>
              <TableCell>Tarih</TableCell>
              <TableCell>Ad</TableCell>
              <TableCell>Notlar</TableCell>
              <TableCell align="right">İşlemler</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {holidays.length === 0 && !loading && (
              <TableRow>
                <TableCell colSpan={4} align="center">{year} için tatil tanımlı değil.</TableCell>
              </TableRow>
            )}
            {holidays.map((h) => (
              <TableRow key={h.id}>
                <TableCell>{h.date}</TableCell>
                <TableCell>{h.name}</TableCell>
                <TableCell>{h.notes || '—'}</TableCell>
                <TableCell align="right">
                  <Stack direction="row" spacing={1} justifyContent="flex-end">
                    <Button size="small" variant="outlined" onClick={() => openEdit(h)}>Düzenle</Button>
                    <Button size="small" variant="outlined" color="error" onClick={() => remove(h)}>Sil</Button>
                  </Stack>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Mobile: one card per holiday */}
      <Stack spacing={1.5} sx={{ display: { xs: 'flex', md: 'none' } }}>
        {holidays.length === 0 && !loading && (
          <Paper sx={{ py: 6, textAlign: 'center' }}>
            <Typography color="text.secondary">{year} için tatil tanımlı değil.</Typography>
          </Paper>
        )}
        {holidays.map((h) => (
          <Card key={h.id}>
            <CardContent>
              <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 1 }}>
                <Box>
                  <Typography variant="h6">{h.name}</Typography>
                  <Typography variant="body2" color="primary" sx={{ fontWeight: 600 }}>
                    {h.date}
                  </Typography>
                </Box>
              </Stack>
              {h.notes && (
                <>
                  <Divider sx={{ my: 1 }} />
                  <Typography variant="body2" color="text.secondary">
                    {h.notes}
                  </Typography>
                </>
              )}
              <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
                <Button size="small" variant="outlined" fullWidth onClick={() => openEdit(h)}>Düzenle</Button>
                <Button size="small" variant="outlined" color="error" fullWidth onClick={() => remove(h)}>Sil</Button>
              </Stack>
            </CardContent>
          </Card>
        ))}
      </Stack>

      <Dialog open={open} onClose={() => !saving && setOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{isEditing ? 'Tatil Düzenle' : 'Yeni Tatil'}</DialogTitle>
        <DialogContent>
          <TextField
            label="Tarih"
            type="date"
            value={form.date}
            onChange={onFormChange('date')}
            fullWidth
            margin="dense"
            InputLabelProps={{ shrink: true }}
            required
          />
          <TextField
            label="Ad"
            value={form.name}
            onChange={onFormChange('name')}
            fullWidth
            margin="dense"
            required
          />
          <TextField
            label="Notlar (opsiyonel)"
            value={form.notes}
            onChange={onFormChange('notes')}
            fullWidth
            margin="dense"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)} disabled={saving}>İptal</Button>
          <Button onClick={submit} variant="contained" disabled={saving}>
            {saving ? <CircularProgress size={18} /> : 'Kaydet'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
